export class BookingValidationError extends Error {
    code = 'VALIDATION_FAILED';
}
export function defaultBookingConfig(pickupMode) {
    return {
        version: 1,
        children: { enabled: false, priceCents: null, description: '' },
        travelers: { name: false, document: false, mobile: false },
        pickup: {
            mode: /HOTEL|DOOR|DESIGNATED_AREA|酒店|接送/i.test(pickupMode ?? '') ? 'HOTEL' : 'NONE',
            description: '',
            collectTime: false,
        },
        accommodation: [],
        vehicles: [],
        extras: [],
    };
}
function object(value) {
    if (!value || typeof value !== 'object' || Array.isArray(value))
        throw new BookingValidationError('配置格式不正确');
    return value;
}
function text(value, limit = 200) {
    if (typeof value !== 'string' || value.length > limit)
        throw new BookingValidationError('文字长度超出限制');
    return value.trim();
}
function integer(value, max = 100000000) {
    if (typeof value !== 'number' || !Number.isSafeInteger(value) || value < 0 || value > max)
        throw new BookingValidationError('请输入有效的数量或金额');
    return value;
}
function bool(value) {
    if (typeof value !== 'boolean')
        throw new BookingValidationError('开关配置不正确');
    return value;
}
function imageUrl(value) {
    const url = text(value, 1000);
    if (url &&
        !/^https:\/\//.test(url) &&
        !/^\/api\/customer\/public\/m2\/media\/[a-f0-9-]+\/content$/.test(url))
        throw new BookingValidationError('请选择产品媒体地址或 HTTPS 图片');
    return url;
}
export function parseBookingConfig(value, pickupMode) {
    if (value == null)
        return defaultBookingConfig(pickupMode);
    const source = object(value), children = object(source.children), travelers = object(source.travelers), pickup = object(source.pickup);
    if (source.version !== 1 || !['NONE', 'HOTEL', 'MEETING_POINT'].includes(String(pickup.mode)))
        throw new BookingValidationError('预订配置版本或接送方式不正确');
    function options(value) {
        if (!Array.isArray(value) || value.length > 20)
            throw new BookingValidationError('每组最多配置 20 个选项');
        const result = value.map((entry) => {
            const item = object(entry), id = text(item.id, 64), title = text(item.title, 60);
            if (!id ||
                !title ||
                !['PERSON', 'ADULT', 'ORDER', 'ROOM_NIGHT'].includes(String(item.charge)))
                throw new BookingValidationError('选项名称、编号和计费方式必填');
            return {
                id,
                title,
                description: text(item.description),
                ...(item.imageUrl === undefined ? {} : { imageUrl: imageUrl(item.imageUrl) }),
                priceCents: integer(item.priceCents),
                charge: item.charge,
                ...(item.minPeople === undefined ? {} : { minPeople: integer(item.minPeople, 99) }),
                ...(item.maxPeople === undefined ? {} : { maxPeople: integer(item.maxPeople, 99) }),
            };
        });
        if (new Set(result.map((item) => item.id)).size !== result.length)
            throw new BookingValidationError('选项编号不能重复');
        return result;
    }
    let hero;
    if (source.hero !== undefined) {
        const value = object(source.hero), imageUrl = text(value.imageUrl, 1000);
        if (imageUrl &&
            !/^https:\/\//.test(imageUrl) &&
            !/^\/api\/customer\/public\/m2\/media\/[a-f0-9-]+\/content$/.test(imageUrl))
            throw new BookingValidationError('头图请选择产品媒体地址或 HTTPS 图片');
        hero = { title: text(value.title, 50), subtitle: text(value.subtitle, 100), imageUrl };
    }
    const result = {
        version: 1,
        children: {
            enabled: bool(children.enabled),
            priceCents: children.priceCents === null ? null : integer(children.priceCents),
            description: text(children.description),
            ...(children.minAge === undefined ? {} : { minAge: integer(children.minAge, 17) }),
            ...(children.maxAge === undefined ? {} : { maxAge: integer(children.maxAge, 17) }),
            ...(children.bed === undefined
                ? {}
                : { bed: enumValue(children.bed, ['NONE', 'ADULT_PRICE', 'SUPPLEMENT']) }),
            ...(children.bedSupplementCents === undefined
                ? {}
                : { bedSupplementCents: integer(children.bedSupplementCents) }),
        },
        travelers: {
            name: bool(travelers.name),
            document: bool(travelers.document),
            mobile: bool(travelers.mobile),
        },
        pickup: {
            mode: pickup.mode,
            description: text(pickup.description, 500),
            collectTime: bool(pickup.collectTime),
        },
        ...(hero ? { hero } : {}),
        accommodation: options(source.accommodation),
        vehicles: options(source.vehicles),
        extras: options(source.extras),
    };
    if (source.childTravelers !== undefined) {
        const fields = object(source.childTravelers);
        result.childTravelers = {
            name: bool(fields.name),
            document: bool(fields.document),
            mobile: bool(fields.mobile),
        };
    }
    if (source.lodging !== undefined) {
        const lodging = object(source.lodging);
        result.lodging = {
            mode: enumValue(lodging.mode, ['NONE', 'INCLUDED', 'CHOICE']),
            summary: text(lodging.summary),
            nights: integer(lodging.nights, 60),
            roomCapacity: integer(lodging.roomCapacity, 8),
        };
        if (result.lodging.mode !== 'NONE' && (!result.lodging.nights || !result.lodging.roomCapacity))
            throw new BookingValidationError('请填写住宿晚数与每间入住人数');
        if (result.lodging.mode === 'NONE')
            result.accommodation = [];
        if (result.lodging.mode === 'INCLUDED' && result.accommodation.some((o) => o.priceCents))
            throw new BookingValidationError('固定住宿应计入套餐价格，不能设置隐藏加价');
        if (result.lodging.mode === 'CHOICE' && !result.accommodation.length)
            throw new BookingValidationError('请添加可选住宿方案');
    }
    if (source.transport !== undefined) {
        const transport = object(source.transport);
        result.transport = {
            mode: enumValue(transport.mode, ['NONE', 'INCLUDED', 'AUTO', 'CHOICE']),
            summary: text(transport.summary),
        };
        if (['NONE', 'INCLUDED'].includes(result.transport.mode))
            result.vehicles = [];
        if (result.transport.mode === 'AUTO') {
            if (!result.vehicles.length ||
                result.vehicles.some((o) => o.priceCents || !o.minPeople || !o.maxPeople || o.minPeople > o.maxPeople))
                throw new BookingValidationError('自动配车须设置有效人数区间，费用应包含在套餐价格内');
            const ranges = [...result.vehicles].sort((a, b) => a.minPeople - b.minPeople);
            if (ranges.some((o, i) => i > 0 && o.minPeople <= ranges[i - 1].maxPeople))
                throw new BookingValidationError('自动配车人数区间不能重叠');
        }
        if (result.transport.mode === 'CHOICE' && !result.vehicles.length)
            throw new BookingValidationError('请添加可选车型');
        if (result.transport.mode === 'CHOICE' &&
            result.vehicles.some((v) => (v.minPeople ?? 1) < 1 || (v.maxPeople ?? 99) < (v.minPeople ?? 1)))
            throw new BookingValidationError('可选车型须设置有效的乘车人数范围');
    }
    if ((result.children.minAge ?? 0) > (result.children.maxAge ?? 17))
        throw new BookingValidationError('儿童年龄范围不正确');
    if (lodgingMode(result) === 'NONE')
        result.children.bed = 'NONE';
    if (source.departures !== undefined) {
        if (!Array.isArray(source.departures) || source.departures.length > 366)
            throw new BookingValidationError('最多配置一年特殊班期');
        result.departures = source.departures.map((entry) => {
            const d = object(entry), date = text(d.date, 10);
            if (!/^\d{4}-\d{2}-\d{2}$/.test(date) ||
                !Number.isFinite(Date.parse(`${date}T12:00:00Z`)) ||
                new Date(`${date}T12:00:00Z`).toISOString().slice(0, 10) !== date)
                throw new BookingValidationError('班期日期不正确');
            if (d.priceCents === 0)
                throw new BookingValidationError('班期成人价格须大于 0');
            return {
                date,
                status: enumValue(d.status, ['OPEN', 'CONFIRM', 'CLOSED']),
                priceCents: d.priceCents === null ? null : integer(d.priceCents),
                childPriceCents: d.childPriceCents === null ? null : integer(d.childPriceCents),
                capacity: d.capacity === null ? null : integer(d.capacity, 9999),
            };
        });
        if (new Set(result.departures.map((d) => d.date)).size !== result.departures.length)
            throw new BookingValidationError('班期日期不能重复');
    }
    if (result.pickup.mode === 'MEETING_POINT' && !result.pickup.description)
        throw new BookingValidationError('请填写集合地点');
    return result;
}
export function parseBookingDetails(value) {
    const source = object(value);
    if (!Array.isArray(source.extraIds) ||
        source.extraIds.length > 20 ||
        !Array.isArray(source.travelers) ||
        source.travelers.length > 99)
        throw new BookingValidationError('出行信息数量超出限制');
    const travelers = source.travelers.map((entry) => {
        const item = object(entry);
        if (item.documentType !== undefined &&
            !['ID_CARD', 'PASSPORT'].includes(String(item.documentType)))
            throw new BookingValidationError('证件类型不正确');
        return {
            ...(item.name === undefined ? {} : { name: text(item.name, 100) }),
            ...(item.mobile === undefined ? {} : { mobile: text(item.mobile, 32) }),
            ...(item.documentType === undefined
                ? {}
                : { documentType: item.documentType }),
            ...(item.documentNumber === undefined
                ? {}
                : { documentNumber: text(item.documentNumber, 40) }),
        };
    });
    return {
        adults: integer(source.adults, 99),
        children: integer(source.children, 99),
        ...(source.childBed === undefined ? {} : { childBed: bool(source.childBed) }),
        ...(source.accommodationId === undefined
            ? {}
            : { accommodationId: text(source.accommodationId, 64) }),
        ...(source.vehicleId === undefined ? {} : { vehicleId: text(source.vehicleId, 64) }),
        extraIds: source.extraIds.map((id) => text(id, 64)),
        travelers,
    };
}
export function quoteBooking(config, selection, unitCents, pricingMode, travelDate) {
    const departure = config.departures?.find((d) => d.date === travelDate);
    if (departure?.status === 'CLOSED')
        throw new BookingValidationError('该班期已停售');
    unitCents = departure?.priceCents ?? unitCents;
    integer(unitCents);
    const adults = integer(selection.adults, 99), children = integer(selection.children, 99), people = adults + children;
    if (adults < 1 || people > 99)
        throw new BookingValidationError('至少需要 1 位成人，出行人数不能超过 99 人');
    if (!config.children.enabled && children)
        throw new BookingValidationError('该套餐未开放儿童预订');
    const lines = pricingMode === 'PER_PERSON'
        ? [
            { label: `成人 × ${adults}`, amount: unitCents * adults },
            ...(children
                ? [
                    {
                        label: `儿童 × ${children}`,
                        amount: (selection.childBed && config.children.bed === 'ADULT_PRICE'
                            ? unitCents
                            : (departure?.childPriceCents ?? config.children.priceCents ?? unitCents)) *
                            children,
                    },
                ]
                : []),
        ]
        : [{ label: '服务套餐', amount: unitCents }];
    if (selection.childBed &&
        (!children ||
            lodgingMode(config) === 'NONE' ||
            !config.children.bed ||
            config.children.bed === 'NONE'))
        throw new BookingValidationError('此方案不支持儿童占床选择');
    if (selection.childBed && config.children.bed === 'SUPPLEMENT')
        lines.push({
            label: `儿童占床 × ${children}`,
            amount: (config.children.bedSupplementCents ?? 0) * children,
        });
    if (selection.childBed && config.children.bed === 'ADULT_PRICE' && pricingMode !== 'PER_PERSON')
        throw new BookingValidationError('按车或套餐计价请选择儿童占床补差规则');
    const add = (options, id, label) => {
        if (!options.length) {
            if (id)
                throw new BookingValidationError(`${label}选项已失效`);
            return;
        }
        const selected = options.find((option) => option.id === id);
        if (!selected)
            throw new BookingValidationError(`请选择${label}`);
        lines.push({
            label: selected.title,
            amount: selected.priceCents *
                (selected.charge === 'PERSON'
                    ? people
                    : selected.charge === 'ADULT'
                        ? adults
                        : selected.charge === 'ROOM_NIGHT'
                            ? Math.ceil((adults + (selection.childBed ? children : 0)) /
                                (config.lodging?.roomCapacity ?? 2)) * (config.lodging?.nights ?? 1)
                            : 1),
        });
    };
    if (lodgingMode(config) === 'CHOICE')
        add(config.accommodation, selection.accommodationId, '住宿方案');
    else if (selection.accommodationId &&
        !config.accommodation.some((o) => o.id === selection.accommodationId && !o.priceCents))
        throw new BookingValidationError('住宿选项已失效');
    if (transportMode(config) === 'CHOICE') {
        const selectedVehicle = config.vehicles.find((v) => v.id === selection.vehicleId);
        if (selectedVehicle && !vehicleFitsPeople(selectedVehicle, people))
            throw new BookingValidationError('所选车型不适合当前出行人数，请重新选择');
        add(config.vehicles, selection.vehicleId, '出行车辆');
    }
    else if (selection.vehicleId)
        throw new BookingValidationError('固定车辆无需选择');
    if (transportMode(config) === 'AUTO' && !matchedVehicle(config, people))
        throw new BookingValidationError('当前人数没有可用的车辆安排');
    if (new Set(selection.extraIds).size !== selection.extraIds.length)
        throw new BookingValidationError('可选服务不能重复');
    for (const id of selection.extraIds)
        add(config.extras, id, '可选服务');
    if (!config.extras.length && selection.extraIds.length)
        throw new BookingValidationError('可选服务已失效');
    const totalCents = lines.reduce((sum, line) => sum + line.amount, 0);
    if (!Number.isSafeInteger(totalCents) || totalCents <= 0 || totalCents > 2147483647)
        throw new BookingValidationError('订单金额超出范围');
    return { totalCents, lines };
}
export function validateBookingTravelers(config, details, pickupAddress, startTime) {
    const required = needsTravelerInformation(config, details.adults, details.children);
    if (details.travelers.length !== (required ? details.adults + details.children : 0))
        throw new BookingValidationError('请填写与出行人数一致的出行人信息');
    details.travelers.forEach((traveler, index) => {
        const fields = travelerFields(config, index >= details.adults);
        if (fields.name && !traveler.name?.trim())
            throw new BookingValidationError(`请填写第 ${index + 1} 位出行人的姓名`);
        if (fields.mobile && !/^1\d{10}$/.test(traveler.mobile ?? ''))
            throw new BookingValidationError(`请填写第 ${index + 1} 位出行人的手机号`);
        if (fields.document && !traveler.documentType)
            throw new BookingValidationError(`请选择第 ${index + 1} 位出行人的证件类型`);
        if (fields.document &&
            !(traveler.documentType === 'ID_CARD' ? /^\d{17}[\dXx]$/ : /^[A-Za-z0-9]{5,20}$/).test(traveler.documentNumber ?? ''))
            throw new BookingValidationError(`请填写第 ${index + 1} 位出行人的有效证件号码`);
        if ((!fields.name && traveler.name) ||
            (!fields.mobile && traveler.mobile) ||
            (!fields.document && (traveler.documentNumber || traveler.documentType)))
            throw new BookingValidationError('请勿提交线路不需要的个人信息');
    });
    if (config.pickup.mode === 'HOTEL' && !pickupAddress?.trim())
        throw new BookingValidationError('请填写接送酒店或地址');
    if (config.pickup.mode === 'HOTEL' &&
        config.pickup.collectTime &&
        !/^([01]\d|2[0-3]):[0-5]\d$/.test(startTime ?? ''))
        throw new BookingValidationError('请选择期望接送时间');
}
export function requiresStandardBooking(config) {
    return (config.children.enabled ||
        Object.values(config.travelers).some(Boolean) ||
        Object.values(config.childTravelers ?? {}).some(Boolean) ||
        config.pickup.mode === 'HOTEL' ||
        (config.departures?.length ?? 0) > 0 ||
        config.accommodation.length > 0 ||
        config.vehicles.length > 0 ||
        config.extras.length > 0);
}
function enumValue(value, choices) {
    if (!choices.includes(value))
        throw new BookingValidationError('配置选项不正确');
    return value;
}
export function lodgingMode(config) {
    return (config.lodging?.mode ??
        (!config.accommodation.length
            ? 'NONE'
            : config.accommodation.length === 1 && !config.accommodation[0].priceCents
                ? 'INCLUDED'
                : 'CHOICE'));
}
export function transportMode(config) {
    return (config.transport?.mode ??
        (!config.vehicles.length
            ? 'NONE'
            : config.vehicles.length === 1 && !config.vehicles[0].priceCents
                ? 'INCLUDED'
                : 'CHOICE'));
}
export function matchedVehicle(config, people) {
    return config.vehicles.find((v) => vehicleFitsPeople(v, people));
}
export function vehicleFitsPeople(vehicle, people) {
    return people >= (vehicle.minPeople ?? 1) && people <= (vehicle.maxPeople ?? 99);
}
export function travelerFields(config, child) {
    return child
        ? (config.childTravelers ?? { ...config.travelers, mobile: false })
        : config.travelers;
}
export function needsTravelerInformation(config, adults, children) {
    return ((adults > 0 && Object.values(travelerFields(config, false)).some(Boolean)) ||
        (children > 0 && Object.values(travelerFields(config, true)).some(Boolean)));
}
export function defaultBookingOption(options) {
    return options.find((o) => o.priceCents === 0)?.id;
}
//# sourceMappingURL=booking-config.js.map